'use client';

import type { EnterHealthMeData } from '@ctrl-enter/types';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:integrations-panel');
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import IntegrationHowToImage from '../assets/integration-how-to.png';
import {
  useDeleteIntegrationMutation,
  useGetIntegrationsQuery,
  useSaveIntegrationMutation,
  useVerifyIntegrationMutation,
} from '../hooks/use-gateway-queries';
import { useElectron } from '../services/electron/electron-provider';
import { cn } from '../utils/cn';
import { toast } from '../utils/toast';
import { Button } from './button';
import { InfoRow, InfoSection } from './info-section';
import { PlusIcon } from './icons/svg';
import { Input } from './input';
import { IntegrationAccountSelectionModal } from './integration-account-selection-modal';
/**
 * IntegrationsPanel component for managing integrations.
 * Allows users to add new integrations and view/delete existing ones.
 */
type VerificationResponse = {
  organization: { id: string; name: string; email?: string | undefined };
  meData: EnterHealthMeData;
  accessToken: string;
  refreshToken?: string | undefined;
};

export const IntegrationsPanel = () => {
  const { external } = useElectron();
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [apiKey, setApiKey] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');
  const [deleteConfirmationId, setDeleteConfirmationId] = React.useState<string | null>(null);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [showAccountModal, setShowAccountModal] = React.useState(false);
  const [verificationData, setVerificationData] = React.useState<VerificationResponse | null>(null);
  const [showApiKeyInfo, setShowApiKeyInfo] = React.useState(false);

  const verifyIntegrationMutation = useVerifyIntegrationMutation();
  const saveIntegrationMutation = useSaveIntegrationMutation();
  const deleteIntegrationMutation = useDeleteIntegrationMutation();
  const {
    data: integrationsData,
    isLoading: isLoadingIntegrations,
    refetch: refetchIntegrations,
    isFetching,
  } = useGetIntegrationsQuery();

  const integrations = React.useMemo(() => {
    return (
      integrationsData?.body?.integrations?.map(int => ({
        id: int.account_id,
        orgName: int.business_name,
        apiKey: int.api_key, // May be undefined for non-admin users
        secretKey: int.secret_key, // May be undefined for non-admin users
      })) || []
    );
  }, [integrationsData]);
  React.useEffect(() => {
    setShowAddForm(integrations.length === 0);
  }, [integrations.length]);

  const features: string[] = [
    'Run eligibility checks',
    'View patient balances',
    'Run insurance discovery',
    'View past claims',
    'Generate a CMS1500 pdf',
    'Create and edit claims',
    'Manage patient collections and payment plans',
    'Get claim status',
  ];

  const handleSave = async () => {
    if (!apiKey.trim() || !secretKey.trim()) return;

    // Prevent verification if integrations are still loading
    // This ensures auth state and queries are fully initialized
    if (isLoadingIntegrations) {
      logger.info('⏳ [INTEGRATIONS-PANEL] Waiting for integrations to load before verifying...');
      toast.error('Please Wait', 'Please wait while we load your integrations');
      return;
    }

    logger.info(
      '🔄 [INTEGRATIONS-PANEL] Starting verification for API key:',
      apiKey.substring(0, 10) + '...'
    );
    setIsVerifying(true);

    try {
      // Verify credentials with backend
      const result = await verifyIntegrationMutation.mutateAsync({
        body: {
          apiKey: apiKey.trim(),
          secretKey: secretKey.trim(),
        },
      });

      logger.info('✅ [INTEGRATIONS-PANEL] Verification result:', {
        status: result.status,
        success: result.body?.success,
      });

      if (result.status === 200 && result.body.success) {
        // Store verification data
        setVerificationData({
          organization: result.body.organization,
          meData: result.body.meData,
          accessToken: result.body.accessToken,
          refreshToken: result.body.refreshToken,
        });

        // Always show account selection modal
        setShowAccountModal(true);
      } else {
        logger.error(
          '❌ [INTEGRATIONS-PANEL] Verification failed - non-200 status or success=false:',
          result
        );
        toast.error('Verification Failed', 'Failed to verify integration credentials');
      }
    } catch (error) {
      logger.error('❌ [INTEGRATIONS-PANEL] Verification error:', error);
      const errorMessage =
        error &&
        typeof error === 'object' &&
        'body' in error &&
        error.body &&
        typeof error.body === 'object' &&
        'message' in error.body
          ? String((error.body as { message: string }).message)
          : 'Invalid API credentials. Please check and try again.';
      toast.error('Verification Failed', errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmationId(id);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmationId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmationId) return;

    try {
      const result = await deleteIntegrationMutation.mutateAsync({
        params: { accountId: deleteConfirmationId },
      });

      if (result.status === 200) {
        // React Query cache invalidation handles the refetch automatically
        setDeleteConfirmationId(null);
        toast.success('Integration Deleted', 'Integration removed successfully');
      }
    } catch (error) {
      logger.error('Failed to delete integration:', error);
      toast.error('Error', 'Failed to delete integration. Please try again.');
    }
  };

  const handleAccountConfirm = async (selectedAccountId: string) => {
    if (!verificationData) return;

    try {
      // Handle both accountAccess and grantedAccountId cases
      const hasAccountAccess =
        verificationData.meData.accountAccess && verificationData.meData.accountAccess.length > 0;

      let accountName: string;
      let roleId: string;
      let logoUrl: string | undefined;

      if (hasAccountAccess) {
        // Find the selected account from meData.accountAccess
        const selectedAccount = verificationData.meData.accountAccess?.find(
          acc => acc.accountId === selectedAccountId
        );

        if (!selectedAccount) {
          toast.error('Error', 'Selected account not found');
          return;
        }

        accountName = selectedAccount.name;
        roleId = selectedAccount.roleId;
        logoUrl = selectedAccount.logoUrl;
      } else {
        // Use personalInfo and grantedAccountId when accountAccess is empty
        accountName = verificationData.meData.personalInfo?.name || 'Unknown Organization';
        roleId = 'user'; // Default role when accountAccess is empty
        logoUrl = undefined;
      }

      // Save integration to WorkOS organization metadata
      const result = await saveIntegrationMutation.mutateAsync({
        body: {
          api_key: apiKey.trim(),
          secret_key: secretKey.trim(),
          business_name: accountName,
          account_id: selectedAccountId,
          role_id: roleId,
          ...(logoUrl && { logo_url: logoUrl }),
        },
      });

      if (result.status === 200) {
        // React Query cache invalidation handles the refetch automatically
        setApiKey('');
        setSecretKey('');
        setShowAddForm(false);
        setShowAccountModal(false);
        setVerificationData(null);

        toast.success('Integration Added', `Successfully added integration for ${accountName}`);
      }
    } catch (error) {
      logger.error('Failed to add integration:', error);

      // Check if it's a 409 conflict (duplicate integration)
      if (error && typeof error === 'object' && 'status' in error && error.status === 409) {
        toast.error(
          'Integration Already Exists',
          'This integration is already configured. Refreshing list...'
        );
        // Force refetch to show the existing integration
        await refetchIntegrations();
        // Close the modal
        setApiKey('');
        setSecretKey('');
        setShowAddForm(false);
        setShowAccountModal(false);
        setVerificationData(null);
        return;
      }

      const errorMessage =
        error &&
        typeof error === 'object' &&
        'body' in error &&
        error.body &&
        typeof error.body === 'object' &&
        'message' in error.body
          ? String((error.body as { message: string }).message)
          : 'Failed to add integration. Please try again.';
      toast.error('Error', errorMessage);
    }
  };

  const handleAccountModalClose = () => {
    setShowAccountModal(false);
    setVerificationData(null);
  };

  const handleClose = () => {
    setApiKey('');
    setSecretKey('');
    setShowAddForm(false);
  };

  const maskApiKey = (key: string | undefined) => {
    if (!key) return '••••••••'; // Hidden for non-admin users
    if (key.length <= 4) return key;
    return '***...' + key.slice(-4);
  };

  // UUIDv4 validation helper
  const isValidUuidFormat = (value: string): boolean => {
    if (!value) return false;
    const uuidv4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidv4Regex.test(value);
  };

  const apiKeyValid = apiKey.trim() ? isValidUuidFormat(apiKey.trim()) : null;
  const secretKeyValid = secretKey.trim() ? isValidUuidFormat(secretKey.trim()) : null;

  return (
    <div className="space-y-6 sm:space-y-1">
      {/* ENTER Platform Integration Card */}
      <InfoSection>
        {/* Header Section */}
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="flex-shrink-0 w-10 h-10 rounded-md bg-muted flex items-center justify-center">
            <svg
              className="w-5 h-5 text-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-foreground">Connect to ENTER Platform</h3>
            <p className="text-xs text-muted-foreground">Unlock AI-powered RCM control</p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          Transform your workflow by connecting CTRL ENTER to your ENTER Platform. Control your
          entire RCM system through natural conversation with AI.
        </p>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {features.map(feature => (
            <div
              key={feature}
              className="flex items-start gap-3 p-3 rounded-xl bg-muted/30 border border-border/20 dark:border-border"
            >
              <div className="flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm text-foreground leading-relaxed">{feature}</span>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
          <Button
            variant="outline"
            size="md"
            onClick={e => {
              e.preventDefault();
              // Open in new window instead of hiding app
              external.openLink('https://www.enter.health/how-to-ctrl-enter', {
                width: 1200,
                height: 800,
                title: 'How to Connect CTRL ENTER',
              });
            }}
            className="w-full sm:w-auto"
          >
            <Info className="w-4 h-4 mr-2" />
            Learn More
          </Button>
          <p className="text-secondary-enhanced text-sm">See step-by-step setup guide</p>
        </div>
      </InfoSection>

      {/* Add Integration Form */}
      {showAddForm && (
        <InfoSection title="Set Up Integration" className="relative">
          {integrations.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="absolute right-4 top-4 h-8 w-8 z-10"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <div className="space-y-4">
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-medium text-foreground">API Key</label>
                  <button
                    type="button"
                    className={cn(
                      'h-6 w-6 rounded-full flex items-center justify-center transition-all',
                      showApiKeyInfo ? 'bg-primary/10 hover:bg-primary/20' : 'hover:bg-muted'
                    )}
                    onClick={() => setShowApiKeyInfo(!showApiKeyInfo)}
                  >
                    <Info
                      className={cn(
                        'h-4 w-4 transition-colors',
                        showApiKeyInfo ? 'text-primary' : 'text-muted-foreground'
                      )}
                    />
                  </button>
                </div>
                <div className="relative">
                  <Input
                    type="text"
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx"
                    className={cn(
                      'w-full font-mono text-sm pr-10',
                      apiKeyValid === false && 'border-destructive focus-visible:ring-destructive',
                      apiKeyValid === true && 'border-green-500 focus-visible:ring-green-500'
                    )}
                    disabled={isVerifying}
                  />
                  {apiKey.trim() && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {apiKeyValid ? (
                        <div className="h-5 w-5 rounded-full bg-green-500/10 flex items-center justify-center">
                          <Check className="h-3 w-3 text-green-500" />
                        </div>
                      ) : (
                        <div className="h-5 w-5 rounded-full bg-destructive/10 flex items-center justify-center">
                          <X className="h-3 w-3 text-destructive" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-1.5 space-y-1">
                  {apiKeyValid === false && (
                    <p className="text-xs text-destructive font-medium">
                      Invalid format - must be a valid UUIDv4 GUID
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Format:{' '}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">
                      xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx
                    </code>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Found in Settings → Account → API Keys
                  </p>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm font-medium text-foreground">Secret Key</label>
                </div>
                <div className="relative">
                  <Input
                    type="password"
                    value={secretKey}
                    onChange={e => setSecretKey(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx"
                    className={cn(
                      'w-full font-mono text-sm pr-10',
                      secretKeyValid === false &&
                        'border-destructive focus-visible:ring-destructive',
                      secretKeyValid === true && 'border-green-500 focus-visible:ring-green-500'
                    )}
                    disabled={isVerifying}
                  />
                  {secretKey.trim() && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {secretKeyValid ? (
                        <div className="h-5 w-5 rounded-full bg-green-500/10 flex items-center justify-center">
                          <Check className="h-3 w-3 text-green-500" />
                        </div>
                      ) : (
                        <div className="h-5 w-5 rounded-full bg-destructive/10 flex items-center justify-center">
                          <X className="h-3 w-3 text-destructive" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-1.5 space-y-1">
                  {secretKeyValid === false && (
                    <p className="text-xs text-destructive font-medium">
                      Invalid format - must be a valid UUIDv4 GUID
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Format:{' '}
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">
                      xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx
                    </code>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Keep this secure - never share it publicly
                  </p>
                </div>
              </div>
            </div>
            {showApiKeyInfo && (
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Info className="h-4 w-4 text-primary" />
                  How to Get Your API Keys
                </h4>
                <ol className="space-y-2 text-sm text-muted-foreground mb-3">
                  <li className="flex gap-2">
                    <span className="font-semibold text-foreground min-w-[20px]">1.</span>
                    <span>Log in to your ENTER Platform account</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-foreground min-w-[20px]">2.</span>
                    <span>
                      Navigate to{' '}
                      <span className="font-medium text-foreground">
                        Settings → Account → API Keys
                      </span>
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-foreground min-w-[20px]">3.</span>
                    <span>
                      Click <span className="font-medium text-foreground">"Create API Key"</span> in
                      the upper right
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold text-foreground min-w-[20px]">4.</span>
                    <span>Copy both keys and paste them above</span>
                  </li>
                </ol>
                <img
                  src={IntegrationHowToImage}
                  alt="Create API Key screenshot"
                  className="w-full border border-border rounded-md shadow-sm"
                />
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSave}
                variant="primary"
                size="lg"
                className="px-8"
                disabled={
                  !apiKey.trim() ||
                  !secretKey.trim() ||
                  apiKeyValid === false ||
                  secretKeyValid === false ||
                  isVerifying ||
                  isLoadingIntegrations
                }
              >
                {isLoadingIntegrations
                  ? 'Loading...'
                  : isVerifying
                    ? 'Verifying...'
                    : 'Verify & Add Integration'}
              </Button>
            </div>
          </div>
        </InfoSection>
      )}

      {/* Integrations List */}
      {integrations.length > 0 && (
        <InfoSection>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Integrations</h3>
              <span className="text-sm text-muted-foreground font-normal">
                ({integrations.length})
              </span>
            </div>
            <Button
              onClick={() => setShowAddForm(true)}
              variant="primary"
              size="icon"
              className="rounded-full"
            >
              <PlusIcon className="w-5 h-5" />
            </Button>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-sm font-semibold text-text-secondary">
              <div className="flex-1 font-medium">Org Name</div>
              <div className="flex-shrink-0 font-medium">API Key</div>
              <div className="w-8 flex-shrink-0">
                {isFetching && (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>
            {/* Scrollable container for integrations list */}
            <div className="max-h-[400px] overflow-y-auto overflow-x-hidden pr-1">
              {integrations.map(integration => (
                <div
                  key={integration.id}
                  className="flex items-center gap-3 py-4 border-t border-border group hover:bg-muted/30 transition-colors px-2 -mx-2 rounded-md"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{integration.orgName}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <code className="text-sm text-muted-foreground font-mono">
                      {maskApiKey(integration.apiKey)}
                    </code>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteClick(integration.id)}
                    className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors sm:opacity-0 sm:group-hover:opacity-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </InfoSection>
      )}

      {/* Account Selection Modal */}
      {verificationData && (
        <IntegrationAccountSelectionModal
          isOpen={showAccountModal}
          onClose={handleAccountModalClose}
          onConfirm={handleAccountConfirm}
          accounts={
            verificationData.meData.accountAccess &&
            verificationData.meData.accountAccess.length > 0
              ? verificationData.meData.accountAccess
              : [
                  {
                    accountId:
                      verificationData.meData.grantedAccountId || verificationData.organization.id,
                    name:
                      verificationData.meData.personalInfo?.name ||
                      verificationData.organization.name,
                    roleId: 'user',
                    logoUrl: undefined,
                  },
                ]
          }
          isLoading={saveIntegrationMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmationId &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 animate-in fade-in duration-200 p-4">
            <div className="bg-background border border-border rounded-lg shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-start gap-4 p-6 border-b border-border">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-foreground mb-1">Delete Integration</h2>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
                <button
                  onClick={handleCancelDelete}
                  className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <p className="text-base text-foreground">
                  Are you sure you want to remove the integration for{' '}
                  <span className="font-semibold text-foreground">
                    {integrations.find(i => i.id === deleteConfirmationId)?.orgName}
                  </span>
                  ?
                </p>
                <div className="bg-muted/50 border border-border rounded-md p-4">
                  <p className="text-sm text-muted-foreground">
                    The AI will no longer be able to access your ENTER Platform data or perform
                    actions on your behalf.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end p-6 border-t border-border bg-muted/20">
                <Button
                  onClick={handleCancelDelete}
                  variant="outline"
                  size="lg"
                  className="px-6"
                  disabled={deleteIntegrationMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  variant="destructive"
                  size="lg"
                  className="px-6"
                  disabled={deleteIntegrationMutation.isPending}
                >
                  {deleteIntegrationMutation.isPending ? 'Deleting...' : 'Delete Integration'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

IntegrationsPanel.displayName = 'IntegrationsPanel';
